import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { DataReaderService } from '@core/lp/data-reader.service';
import { LpProjectSettingsService } from '@core/lp/lp-project-settings.service';
import { ISaveState, SAVE_SERVICE, WordProgressRecord } from '@core/saves/saves.interface';
import { LpVariablesService } from '@features/lp/lp-variables/lp-variables.service';
import { AdaptivityService } from '@features/wk8/adaptivity.service';
import { Wk8WordService } from '@features/wk8/core/wk8-words/wk8-word-service.service';
import {
  LpWk8Book,
  LpWk8BookSentence,
  LpWk8BookSentencePart,
  LpWk8BookSentencePartFilter,
  LpWk8Planet,
  LpWoordData,
} from '@typings/lp/generated';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Wk8FilterService } from './wk8-filter.service';

class SaveModifier {
  private readonly save: ISaveState;

  public constructor(
    save: ISaveState,
    private readonly ctx: { planets: LpWk8Planet[] },
  ) {
    this.save = structuredClone(save);
  }

  private getWp(part: LpWk8BookSentencePartFilter): WordProgressRecord {
    let wp = this.save.wordProgress.find((wp) => wp.uid === part.uid);
    if (!wp) {
      wp = { uid: part.uid };
      this.save.wordProgress.push(wp as WordProgressRecord);
    }
    return wp;
  }

  public registerWord(part: LpWk8BookSentencePartFilter, word: string | undefined): this {
    const wp = this.getWp(part);

    if (word) {
      wp.word = word;
      wp.finished_timestamp = new Date().toISOString();
    } else {
      delete wp.word;
      delete wp.finished_timestamp;
    }

    return this;
  }

  public registerOptions(part: LpWk8BookSentencePartFilter, options: LpWoordData[]) {
    const wp = this.getWp(part);
    wp.savedFilterOptions = options.map((o) => o.uid);
  }

  private getBookCompletionTimestamp(book: LpWk8Book): string | undefined {
    const progressRecords = book.sentences
      .flatMap((sentence) => sentence.parts.filter((part) => part.dataType === 'Wk8BookSentencePartFilter'))
      .map((wp) => this.getWp(wp));
    if (progressRecords.some((wp) => wp.word === undefined)) return undefined;

    const times = progressRecords.map((wp) => new Date(wp.finished_timestamp ?? 0).getTime());
    if (times.length === 0) return undefined;

    const max = Math.max(...times);
    return new Date(max).toISOString();
  }

  private updatePlanetProgress() {
    this.save.planetProgress = [];
    for (const planet of this.ctx.planets) {
      this.save.planetProgress.push({
        planetUid: planet.uid,
        // found a bug here: the book's finished_timestamp had a typo in the original code, it was 'finished_timestap'
        books: planet.books.map((book) => ({ uid: book.uid, finished_timestamp: this.getBookCompletionTimestamp(book) })),
      });
    }
  }

  public modify(): ISaveState {
    this.updatePlanetProgress();

    return this.save;
  }
}

@Injectable({
  providedIn: 'root',
})
export class Wk8BookService {
  _lpVariableService = inject(LpVariablesService);
  _saveService = inject(SAVE_SERVICE);
  _filterService = inject(Wk8FilterService);
  _wordService = inject(Wk8WordService);
  _adaptivityService = inject(AdaptivityService);
  _dataReaderService = inject(DataReaderService);
  _lpProjectSettingsService = inject(LpProjectSettingsService);

  wordProgressCache = new Map<string, WordProgressRecord>();

  saveLoaded = signal(false);
  saveLoaded$ = toObservable(this.saveLoaded);

  constructor() {
    this._saveService.load().then((save) => this.updateCacheFromSave(save));
  }

  public async waitForLoad() {
    if (this.saveLoaded()) {
      return;
    }
    await firstValueFrom(this.saveLoaded$.pipe(filter((loaded) => loaded)));
  }

  public getSentencePartValue(sentencePart: LpWk8BookSentencePart): string | undefined {
    if (!this.saveLoaded()) {
      throw new Error('Wk8BookService not initialized, please await init() first.');
    }
    switch (sentencePart.dataType) {
      case 'Wk8BookSentencePartPlain':
        return sentencePart.text;
      case 'Wk8BookSentencePartFilter':
        return this.wordProgressCache.get(sentencePart.uid)?.word;
    }
  }

  public getBookCompletionPercentage(book: LpWk8Book): number {
    if (!this.saveLoaded()) {
      throw new Error('Wk8BookService not initialized, please await init() first.');
    }

    const gapUids = new Set(
      book.sentences
        .flatMap((sentence) => sentence.parts)
        .filter((part) => part.dataType === 'Wk8BookSentencePartFilter')
        .map((part) => part.uid),
    );
    const totalGaps = gapUids.size;
    const completedGaps = Array.from(gapUids).filter((uid) => this.wordProgressCache.get(uid)?.word !== undefined).length;
    return completedGaps / totalGaps;
  }

  public isBookComplete(book: LpWk8Book): boolean {
    return book.sentences.every((sentence) => this.isSentenceComplete(sentence));
  }

  public isSentenceComplete(sentence: LpWk8BookSentence): boolean {
    const variablePartUids = sentence.parts
      .filter((part) => part.dataType === 'Wk8BookSentencePartFilter') //
      .map((part) => part.uid);

    return variablePartUids.every((partUid) => this.wordProgressCache.get(partUid)?.word !== undefined);
  }

  public async setOpenSentencePartValue(sentencePart: LpWk8BookSentencePartFilter, value: string | undefined) {
    if (!this.saveLoaded()) {
      throw new Error('Wk8BookService not initialized, please await init() first.');
    }

    const oldSave = await this._saveService.load();
    const newSave = new SaveModifier(oldSave, { planets: (await this._lpProjectSettingsService.getSettings()).woordkr8.planets })
      .registerWord(sentencePart, value)
      .modify();
    this.updateCacheFromSave(newSave);
    await this._saveService.save(newSave);
  }

  public async getOpenSentencePartOptions(sentencePart: LpWk8BookSentencePartFilter) {
    if (!this.saveLoaded()) {
      throw new Error('Wk8BookService not initialized, please await init() first.');
    }

    return this.getOptionsForFilterPart(sentencePart, { max: sentencePart.max ?? 3 });
  }

  private updateCacheFromSave(save: ISaveState) {
    const wordProgress = save.wordProgress ?? [];
    wordProgress.forEach((record) => {
      this.wordProgressCache.set(record.uid, record);
    });
    this.saveLoaded.set(true);
  }

  private getOptionsForFilterPart(filterPart: LpWk8BookSentencePartFilter, options?: { min: number } | { max: number }) {
    const words = this._wordService.words();
    if (words.length === 0) {
      throw new Error('No words available to filter');
    }
    let filtered = this._filterService.applyFilter(words, filterPart.filter);
    if (options && 'min' in options && filtered.length < options.min) {
      console.warn(
        `Filter '${filterPart.name}' found only ${filtered.length} words, filling up to ${options.min} words with adaptive choice.`,
      );
      filtered = [
        ...filtered,
        ...this._adaptivityService.adaptiveChoice(
          words.filter((w) => !filtered.some((fw) => fw.uid === w.uid)),
          options.min - filtered.length,
        ),
      ];
    }
    if (options && 'max' in options) return filtered.slice(0, options.max);

    return filtered;
  }

  public async getBookForSentence(sentence: LpWk8BookSentence): Promise<LpWk8Book> {
    const books = await this._dataReaderService.getInstancesByType('Wk8Book');
    const book = books.find((book) => book.sentences.some((s) => s.uid === sentence.uid));
    if (!book) {
      throw new Error(`No book found for sentence with uid ${sentence.uid}`);
    }
    return book;
  }
}
